import { ReactTable } from "@bphxd/ds-core-react";
import { Download24, Edit64, Preview24, Trash16,CircleCheck32 } from "@bphxd/ds-core-react/lib/icons";
import { RowSelectionState, ColumnDef } from "@tanstack/react-table";
import {
    Dispatch,
    SetStateAction,
    useEffect,
    useMemo,
    useState,
    useCallback,
} from "react";
import moment from "moment";
import { Modal, Button, Input, Label, ModalFooter } from "reactstrap";
import { downloadSingle, viewDrn } from "../../api/DRN/drndownload";
import axios from "axios";
import "./ACTable.scss";
import { useMsal } from "@azure/msal-react";
import { deleteSelectedDrn } from "../../api/DRN/drndelete";
import { validateAnnualCertificate } from "../../api/ACertificate/annualCertificate";

interface AnnualCertificate {
    id: string;
    SoldToName: string;
    SoldToId: string;
    status: string;
    startDate: string;
    expiryDate: string;
    uploadedDate: string;
    approval_date: string;
    approved_by: string;
    document_type?: string;
    document_link?: string;
}

export default function ACTable({
    rowData,
    setSelectedFlatRowsVals,
    rowSelection,
    setRowSelection,
}: {
    rowData?: AnnualCertificate[];
    setSelectedFlatRowsVals?: Dispatch<SetStateAction<AnnualCertificate[]>>;
    rowSelection?: RowSelectionState;
    setRowSelection?: React.Dispatch<React.SetStateAction<RowSelectionState>>;
}) {
    const [selectedFlatRows, setSelectedFlatRows] = useState<AnnualCertificate[]>([]);
    const [randomKeyTable, setRandomKeyTable] = useState<number>(0);
    const [tableData, setTableData] = useState<AnnualCertificate[]>(rowData || []);
    const [modalOpen, setModalOpen] = useState<boolean>(false);
    const [modalData, setModalData] = useState<AnnualCertificate | undefined>();
    const [modalRowIndex, setModalRowIndex] = useState<number | null>(null);
    const [modalErrors, setModalErrors] = useState<{ [key: string]: string }>({});
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [uploadedFile, setUploadedFile] = useState<File | null>(null);
    
    // Delete single document states
    const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
    const [docToDelete, setDocToDelete] = useState<AnnualCertificate | null>(null);
    const [isDeleting, setIsDeleting] = useState<boolean>(false);
    
    // Success message modal states
    const [successModalOpen, setSuccessModalOpen] = useState<boolean>(false);
    const [successMessage, setSuccessMessage] = useState<string>("");
    const [successTitle, setSuccessTitle] = useState<string>("");
    
    const userRole = sessionStorage.getItem("role");
    const isAdmin = ["admin", "itr_user"].includes((userRole || "").toLowerCase());
    const isITSupport =  userRole?.toLowerCase() === "admin";   
     const token = sessionStorage.getItem("jwt");
    const statusOptions = ["Pending", "Valid", "Invalid"];
    const { instance, accounts } = useMsal();
    const accessTokenRequest = {
        account: accounts[0],
        scopes: ["User.Read"],
    };

    const userEmail = accessTokenRequest.account.username;
    const getTodayDate = () => {
        const today = new Date();
        today.setMonth(today.getMonth() + 3);
        return today.toISOString().split('T')[0];
      };
    // Convert file to base64
    const convertFileToBase64 = (file: File): Promise<string> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => {
                const result = reader.result as string;
                const base64String = result.split(",")[1];
                resolve(base64String);
            };
            reader.onerror = (error) => reject(error);
        });
    };

    // Update table data when rowData changes
    useEffect(() => {
        if (rowData && rowData.length > 0) {
            setTableData(rowData);
            setRandomKeyTable(Math.floor(Math.random() * 100));
            console.log("Table data updated with", rowData.length, "records");
        }
    }, [rowData]);

    // Update selected flat rows
    useEffect(() => {
        if (setSelectedFlatRowsVals) {
            setSelectedFlatRowsVals(selectedFlatRows);
        }
    }, [selectedFlatRows, setSelectedFlatRowsVals]);

    // Reset row selection
    useEffect(() => {
        if (setRowSelection) {
            setRowSelection({});
        }
    }, [setRowSelection]);

    // Download PDF
    const downloadPdf = (document: AnnualCertificate) => {
        const docArray = [
            {
                document_id: document.id,
                document_type: document.document_type || "Annual Certificate",
            },
        ];
        downloadSingle(
            docArray,
            document.id,
            document.SoldToId,
            document.document_type || "Annual Certificate",
            token
        );
    };

    // Preview document
    const preview = async (url: string) => {
        try {
            const previewUrl: any = await viewDrn(url, token);
            window.open(previewUrl, "_blank", "width=700,height=700");
        } catch (error) {
            console.error("Error previewing document:", error);
            alert("Error opening preview. Please try again.");
        }
    };

    // Get background color for status
    const getBackgroundColor = (status: string) => {
        switch (status?.toLowerCase()) {
            case "pending":
                return "#fff3cd";
            case "valid":
                return "#d4edda";
            case "invalid":
                return "#f8d7da";
            case "expired":
                return "#f8d7da";
            default:
                return "#fff3cd";
        }
    };

    // Render status with color
    const renderStatusDisplay = (props: any) => {
        const currentValue = props.getValue();
        return (
            <span
                style={{
                    padding: "4px 8px",
                    borderRadius: "4px",
                    backgroundColor: getBackgroundColor(currentValue),
                    display: "inline-block",
                    fontWeight: "500",
                }}
            >
                {currentValue}
            </span>
        );
    };

    // Format date display
    const renderDateDisplay = (value: any) => {
        return value ? moment(value).format("DD MMM YYYY") : "-";
    };

    // Handle edit row
    const handleEditRow = (row: any) => {
        setModalData({ ...row.original });
        setModalRowIndex(row.index);
        setUploadedFile(null);
        setModalErrors({});
        setModalOpen(true);
    };

    // Handle modal field change
    const handleModalFieldChange = (
        fieldName: keyof AnnualCertificate,
        value: any
    ) => {
        if (modalData) {
            setModalData({
                ...modalData,
                [fieldName]: value,
            });
            setModalErrors({
                ...modalErrors,
                [fieldName]: "",
            });
        }
    };

    // Handle file upload
    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files?.[0]) {
            const file = e.target.files[0];
            const validExtensions = ["pdf", "doc", "docx", "jpg", "jpeg", "png"];
            const fileExtension = file.name.split(".").pop()?.toLowerCase();

            if (fileExtension && validExtensions.includes(fileExtension)) {
                setUploadedFile(file);
                setModalErrors({ ...modalErrors, file: "" });
            } else {
                setModalErrors({
                    ...modalErrors,
                    file: "Invalid file type. Allowed: PDF, DOC, DOCX, JPG, PNG",
                });
            }
        }
    };

    // Validate form
    const validateForm = (): boolean => {
        console.log("Validating form with modalData:", modalData);
        const errors: { [key: string]: string } = {};

        if (!modalData) return false;

        const requiredFields = [
            { key: "SoldToName", label: "Customer Name" },
            { key: "SoldToId", label: "Customer Code" },
            { key: "status", label: "Status" },
            { key: "startDate", label: "Start Date" },
            { key: "expiryDate", label: "Expiry Date" },
            { key: "uploadedDate", label: "Uploaded Date" },
        ];

        requiredFields.forEach(({ key, label }) => {
            const value = modalData[key as keyof AnnualCertificate];
            if (!value || (typeof value === "string" && value.trim() === "")) {
                errors[key] = `${label} is required`;
            }
        });

        if (modalData.status && modalData.status.toLowerCase() === "pending") {
            errors.status = "Status must be Valid or Invalid";
        }

        if (modalData.startDate && modalData.expiryDate) {
            if (
                moment(modalData.startDate).isAfter(moment(modalData.expiryDate))
            ) {
                errors.expiryDate = "Expiry date must be after start date";
            }
        }

        setModalErrors(errors);
        return Object.keys(errors).length === 0;
    };

    // Close modal
    const closeModal = () => {
        setModalOpen(false);
        setModalData(undefined);
        setModalRowIndex(null);
        setUploadedFile(null);
        setModalErrors({});
        setIsSubmitting(false);
    };

    // Update row
    const updateRow = async () => {
        if (!modalData) return;

        if (!validateForm()) {
            return;
        }

        setIsSubmitting(true);

        try {
            const certificateData = {
                id: modalData.id,
                SoldToName: modalData.SoldToName,
                SoldToId: modalData.SoldToId,
                status: modalData.status,
                startDate: modalData.startDate,
                expiryDate: modalData.expiryDate,
                uploadedDate: modalData.uploadedDate,
                approval_date: new Date().toISOString().split("T")[0],
                approved_by: userEmail,
                document_type: modalData.document_type || "Annual Certificate",
            };

            const formData = new FormData();
            Object.entries(certificateData).forEach(([key, value]) => {
                formData.append(key, String(value ?? ""));
            });

            console.log("========== ANNUAL CERTIFICATE DATA LOG ==========");
            console.log("Mode: EDIT");
            console.log("Payload:", certificateData);
            const response = await validateAnnualCertificate(formData, token);
            
            // Update table data with new values
            const updatedData = [...tableData];
            if (modalRowIndex !== null && modalRowIndex >= 0) {
                updatedData[modalRowIndex] = {
                    ...modalData,
                    approved_by: userEmail,
                    approval_date: new Date().toISOString().split("T")[0],
                };
            }

            setTableData(updatedData);
            setRandomKeyTable(Math.floor(Math.random() * 100));

            // Show success modal instead of alert
            setSuccessTitle("Success");
            setSuccessMessage("Annual Certificate updated successfully!");
            setSuccessModalOpen(true);
            closeModal();
        } catch (error) {
            console.error("Error saving row:", error);
            const errorMessage = axios.isAxiosError(error)
                ? error.response?.data?.message || error.message
                : "Please try again.";
            setModalErrors({
                ...modalErrors,
                submit: `Failed to save record. ${errorMessage}`,
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    // Handle delete single document
    const handleDeleteDoc = async () => {
        if (!docToDelete) return;

        setIsDeleting(true);
        try {
            const payload = {
                updatedBy: userEmail,
                record: [
                    {
                        document_id: docToDelete.id,
                        document_type: docToDelete.document_type || "Annual Certificate",
                    },
                ],
            };

            console.log("Deleting document with payload:", payload);

            // Call the delete API
            await deleteSelectedDrn(payload, token);

            // Remove from table data
            const updatedData = tableData.filter((item) => item.id !== docToDelete.id);
            setTableData(updatedData);
            setRandomKeyTable(Math.floor(Math.random() * 100));

            // Show success modal instead of alert
            setSuccessTitle("Success");
            setSuccessMessage("Document deleted successfully!");
            setSuccessModalOpen(true);
            setDeleteModalOpen(false);
            setDocToDelete(null);
        } catch (error) {
            console.error("Error deleting document:", error);
            const errorMessage = axios.isAxiosError(error)
                ? error.response?.data?.message || error.message
                : "Please try again.";
            alert(`Failed to delete document. ${errorMessage}`);
        } finally {
            setIsDeleting(false);
        }
    };

    // Table columns definition
    const cols: {
        accessorKey?: string;
        header: any;
        cell?: any;
        sortingFn?: any;
        id?: any;
    }[] = [
        ...(isAdmin
            ? [
                {
                    id: "actions",
                    header: () => {
                        return <div className="alignVDheader">Actions</div>;
                    },
                    cell: ({ row }: any) => {
                        return (
                            <div className="ms-3 doctypeCell">
                                <div>
                                    <Edit64
                                        style={{
                                            color: "black",
                                            cursor: "pointer",
                                            width: "18px",
                                            height: "18px",
                                        }}
                                        className="action-btn"
                                        onClick={() => handleEditRow(row)}
                                        title="Edit"
                                    />
                                </div>
                                <div>
                                {isITSupport && (
                                    <div>
                                        <Trash16
                                            style={{
                                                color: "red",
                                                cursor: "pointer",
                                                width: "18px",
                                                height: "18px",
                                            }}
                                            className="action-btn"
                                            onClick={() => {
                                                setDocToDelete(row.original);
                                                setDeleteModalOpen(true);
                                            }}
                                            title="Delete"
                                        />
                                    </div>
                                )}
                                </div>
                            </div>
                        );
                    },
                },
            ]
            : []),
        {
            id: "view&download",
            header: () => {
                return <div className="alignVDheader">View / Download</div>;
            },
            cell: ({ row }: any) => {
                return (
                    <div className="ms-3 doctypeCell">
                        <div>
                            {row.original.document_link && (
                                <Preview24
                                    style={{ color: "black", cursor: "pointer" }}
                                    className="dwn-view-btn"
                                    onClick={() => {
                                        preview(row.original.document_link);
                                    }}
                                    title="Preview"
                                />
                            )}
                        </div>
                        <div>
                            {row.original.document_link && (
                                <Download24
                                    style={{ color: "black", cursor: "pointer" }}
                                    className="dwn-view-btn"
                                    onClick={() => {
                                        downloadPdf(row.original);
                                    }}
                                    title="Download"
                                />
                            )}
                        </div>
                    </div>
                );
            },
        },
        {
            accessorKey: "SoldToId",
            header: "Customer Code",
            sortingFn: "alphanumeric",
            cell: (props: any) => {
                return <div>{props.getValue()}</div>;
            },
        },
        {
            accessorKey: "SoldToName",
            header: "Customer Name",
            cell: (props: any) => {
                return <div>{props.getValue()}</div>;
            },
        },
        {
            accessorKey: "startDate",
            header: "Start Date",
            cell: (props: any) => {
                return <div>{renderDateDisplay(props.getValue())}</div>;
            },
        },
        {
            accessorKey: "expiryDate",
            header: "Expiry Date",
            cell: (props: any) => {
                return <div>{renderDateDisplay(props.getValue())}</div>;
            },
        },
        {
            accessorKey: "uploadedDate",
            header: "Uploaded Date",
            cell: (props: any) => {
                return <div>{renderDateDisplay(props.getValue())}</div>;
            },
        },
        {
            accessorKey: "approval_date",
            header: "Approval Date",
            cell: (props: any) => {
                return <div>{renderDateDisplay(props.getValue())}</div>;
            },
        },
        {
            accessorKey: "approved_by",
            header: "Approved By",
            cell: (props: any) => {
                return <div>{props.getValue() || "-"}</div>;
            },
        },
        {
            accessorKey: "status",
            header: " Certification Status",
            cell: renderStatusDisplay,
        },
    ];

    const columns = useMemo<ColumnDef<AnnualCertificate>[]>(() => cols, [isAdmin]);

    return (
        <>
            {isLoading ? (
                <div className="ac-table-loading">
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Loading certificates...
                </div>
            ) : tableData.length === 0 ? (
                <div className="ac-table-empty">
                    <p>No data found! Please check search parameter</p>
                </div>
            ) : (
                <div className="ac-table-container">
                    <ReactTable<AnnualCertificate>
                        tableClassName="table-main-class"
                        columns={columns}
                        data={tableData}
                        striped={true}
                        responsive={true}
                        enableRowSelection={false}
                        key={randomKeyTable}
                    />
                </div>
            )}

            {/* Edit Modal */}
            <Modal isOpen={modalOpen} toggle={closeModal} centered size="lg">
                <div className="modal-header border-bottom">
                    <h5 className="mb-0">Validate Annual Certificate</h5>
                    <button
                        type="button"
                        className="btn-close"
                        onClick={closeModal}
                        disabled={isSubmitting}
                    ></button>
                </div>
                <div
                    className="modal-body bg-light"
                    style={{ maxHeight: "70vh", overflowY: "auto", padding: "20px" }}
                >
                    {modalData && (
                        <div>
                            {modalErrors.submit && (
                                <div
                                    className="alert alert-danger alert-dismissible fade show"
                                    role="alert"
                                >
                                    {modalErrors.submit}
                                    <button
                                        type="button"
                                        className="btn-close"
                                        onClick={() =>
                                            setModalErrors({ ...modalErrors, submit: "" })
                                        }
                                        disabled={isSubmitting}
                                    />
                                </div>
                            )}

                            <div
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: "1fr 1fr",
                                    gap: "16px",
                                }}
                            >
                                {/* Customer Code */}
                                <div>
                                    <Label className="fw-bold mb-2">Customer Code</Label>
                                    <Input
                                        bsSize="sm"
                                        type="text"
                                        value={modalData.SoldToId}
                                        onChange={(e) =>
                                            handleModalFieldChange("SoldToId", e.target.value)
                                        }
                                        placeholder="Enter customer code"
                                        disabled={true}
                                        style={{ backgroundColor: "#e9ecef", cursor: "not-allowed" }}
                                        className={modalErrors.SoldToId ? "is-invalid" : ""}
                                    />
                                    {modalErrors.SoldToId && (
                                        <small className="text-danger d-block mt-1">
                                            {modalErrors.SoldToId}
                                        </small>
                                    )}
                                </div>

                                {/* Customer Name */}
                                <div>
                                    <Label className="fw-bold mb-2">Customer Name</Label>
                                    <Input
                                        bsSize="sm"
                                        type="text"
                                        value={modalData.SoldToName}
                                        onChange={(e) =>
                                            handleModalFieldChange("SoldToName", e.target.value)
                                        }
                                        placeholder="Enter customer name"
                                        disabled={true}
                                        style={{ backgroundColor: "#e9ecef", cursor: "not-allowed" }}
                                        className={modalErrors.SoldToName ? "is-invalid" : ""}
                                    />
                                    {modalErrors.SoldToName && (
                                        <small className="text-danger d-block mt-1">
                                            {modalErrors.SoldToName}
                                        </small>
                                    )}
                                </div>

                                {/* Start Date */}
                                <div>
                                    <Label className="fw-bold mb-2">
                                        Start Date
                                        <span style={{ color: "red", marginLeft: "4px" }}>*</span>
                                    </Label>
                                    <Input
                                        bsSize="sm"
                                        type="date"
                                        value={
                                            modalData.startDate
                                                ? moment(modalData.startDate).format("YYYY-MM-DD")
                                                : ""
                                        }
                                        onChange={(e) =>
                                            handleModalFieldChange("startDate", e.target.value)
                                        }
                                        disabled={isSubmitting}
                                        className={modalErrors.startDate ? "is-invalid" : ""}
                                    />
                                    {modalErrors.startDate && (
                                        <small className="text-danger d-block mt-1">
                                            {modalErrors.startDate}
                                        </small>
                                    )}
                                </div>

                                {/* Expiry Date */}
                                <div>
                                    <Label className="fw-bold mb-2">
                                        Expiry Date
                                        <span style={{ color: "red", marginLeft: "4px" }}>*</span>
                                    </Label>
                                    <Input
                                        bsSize="sm"
                                        type="date"
                                        min={getTodayDate()}
                                        value={
                                            modalData.expiryDate
                                                ? moment(modalData.expiryDate).format("YYYY-MM-DD")
                                                : ""
                                        }
                                        onChange={(e) =>
                                            handleModalFieldChange("expiryDate", e.target.value)
                                        }
                                        disabled={isSubmitting}
                                        className={modalErrors.expiryDate ? "is-invalid" : ""}
                                    />
                                    {modalErrors.expiryDate && (
                                        <small className="text-danger d-block mt-1">
                                            {modalErrors.expiryDate}
                                        </small>
                                    )}
                                </div>

                                {/* Uploaded Date */}
                                <div>
                                    <Label className="fw-bold mb-2">Uploaded Date</Label>
                                    <Input
                                        bsSize="sm"
                                        type="date"
                                        value={
                                            modalData.uploadedDate
                                                ? moment(modalData.uploadedDate).format("YYYY-MM-DD")
                                                : ""
                                        }
                                        onChange={(e) =>
                                            handleModalFieldChange("uploadedDate", e.target.value)
                                        }
                                        disabled={true}
                                        style={{ backgroundColor: "#e9ecef", cursor: "not-allowed" }}
                                        className={modalErrors.uploadedDate ? "is-invalid" : ""}
                                    />
                                    {modalErrors.uploadedDate && (
                                        <small className="text-danger d-block mt-1">
                                            {modalErrors.uploadedDate}
                                        </small>
                                    )}
                                </div>

                                {/* Status */}
                                <div>
                                    <Label className="fw-bold mb-2">
                                        Certification Status
                                        <span style={{ color: "red", marginLeft: "4px" }}>*</span>
                                    </Label>
                                    <select
                                        className={`form-select form-select-sm ${
                                            modalErrors.status ? "is-invalid" : ""
                                        }`}
                                        value={modalData.status}
                                        onChange={(e) =>
                                            handleModalFieldChange("status", e.target.value)
                                        }
                                        style={{ padding: "6px 8px" }}
                                        disabled={isSubmitting}
                                    >
                                        <option value="">Select Status</option>
                                        {statusOptions.map((option) => (
                                            <option key={option} value={option}>
                                                {option}
                                            </option>
                                        ))}
                                    </select>
                                    {modalErrors.status && (
                                        <small className="text-danger d-block mt-1">
                                            {modalErrors.status}
                                        </small>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
                <div className="modal-footer">
                    <Button color="secondary" onClick={closeModal} disabled={isSubmitting}>
                        Cancel
                    </Button>
                    <Button color="primary" onClick={updateRow} disabled={isSubmitting}>
                        {isSubmitting ? (
                            <>
                                <span
                                    className="spinner-border spinner-border-sm me-2"
                                    role="status"
                                    aria-hidden="true"
                                ></span>
                                Updating...
                            </>
                        ) : (
                            "Update"
                        )}
                    </Button>
                </div>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal isOpen={deleteModalOpen} toggle={() => setDeleteModalOpen(false)} centered>
                <div className="modal-header border-bottom">
                    <h5 className="mb-0">Confirm Delete</h5>
                    <button
                        type="button"
                        className="btn-close"
                        onClick={() => setDeleteModalOpen(false)}
                        disabled={isDeleting}
                    ></button>
                </div>
                <div className="modal-body">
                    <p>
                        Are you sure you want to delete this document for{" "}
                        <strong>{docToDelete?.SoldToName}</strong>?
                    </p>
                    <p className="text-muted mb-0">
                        Customer Code: {docToDelete?.SoldToId}
                    </p>
                </div>
                <div className="modal-footer">
                    <Button
                        color="secondary"
                        onClick={() => setDeleteModalOpen(false)}
                        disabled={isDeleting}
                    >
                        Cancel
                    </Button>
                    <Button color="danger" onClick={handleDeleteDoc} disabled={isDeleting}>
                        {isDeleting ? (
                            <>
                                <span
                                    className="spinner-border spinner-border-sm me-2"
                                    role="status"
                                    aria-hidden="true"
                                ></span>
                                Deleting...
                            </>
                        ) : (
                            "Delete"
                        )}
                    </Button>
                </div>
            </Modal>

            {/* Success Message Modal */}
            <Modal isOpen={successModalOpen} toggle={() => setSuccessModalOpen(false)} centered>
                <div className="modal-header border-bottom">
                    <h5 className="mb-0">{successTitle}</h5>
                    <button
                        type="button"
                        className="btn-close"
                        onClick={() => setSuccessModalOpen(false)}
                    ></button>
                </div>
                <div className="modal-body">
                    <div style={{ textAlign: "center", padding: "20px 0" }}>
                        <CircleCheck32 color="#28a745" style={{ width: "48px", height: "48px" }} />
                        <p style={{ fontSize: "16px", color: "#333", fontWeight: "500" }}>
                            {successMessage}
                        </p>
                    </div>
                </div>
                <div className="modal-footer">
                    <Button
                        color="primary"
                        onClick={() => setSuccessModalOpen(false)}
                    >
                        Close
                    </Button>
                </div>
            </Modal>
        </>
    );
}